/**
 * Product Tags Types
 *
 * Types for the product tagging/attributes system that allows
 * admins to attach metadata (tags) to baskets and portfolios.
 *
 * Tags help investors understand product characteristics:
 * - Risk level (1-10 score + descriptive type)
 * - Investment focus/strategy
 * - Minimum investment amount
 * - Featured status
 *
 * Access model:
 * - ADMIN: Full CRUD (create, read, update, delete)
 * - PROVIDER: Read-only (may get write access later)
 * - INVESTOR: Read-only (helps with investment decisions)
 */

// ============================================================
// Enum Types (for type safety)
// ============================================================

/**
 * Investment focus/strategy type.
 * Describes the overall investment approach of a product.
 */
export type FocusType =
  | 'Aggressive Growth'
  | 'Growth'
  | 'Balanced'
  | 'Income'
  | 'Capital Preservation';

/**
 * Risk type classification.
 * Descriptive label for the risk level of a product.
 */
export type RiskTypeValue =
  | 'Extremely High'
  | 'High'
  | 'Medium'
  | 'Low'
  | 'Very Low';

/**
 * Product type discriminator.
 * Identifies whether settings belong to a basket or portfolio.
 */
export type ProductType = 'BASKET' | 'PORTFOLIO';

// ============================================================
// Settings JSON (what we send/receive in settingsJson)
// ============================================================

/**
 * The actual tag data stored in settingsJson.
 * All fields are optional - products may have any subset of tags.
 *
 * This interface defines the frontend contract for tag structure.
 * Backend stores this as free-form JSON in the settingsJson field.
 */
export interface ProductTagsData {
  /** Risk score from 1 (lowest) to 10 (highest) */
  risk?: number;

  /** Investment strategy/focus type */
  focus?: FocusType;

  /** Descriptive risk classification */
  riskType?: RiskTypeValue;

  /** Minimum recommended investment amount in USD */
  minInvestment?: number;

  /** Whether this product is featured/promoted */
  featured?: boolean;
}

// ============================================================
// API Response Types (matches openapi.json ProductSettings schema)
// ============================================================

/**
 * Single product settings from API.
 * Matches the ProductSettings schema in openapi.json.
 *
 * NOTE: providerId is NOT included in the API response.
 * If needed, track it from the request context.
 */
export interface ProductSettings {
  /** Type of product (BASKET or PORTFOLIO) */
  productType: ProductType;

  /** ID of the basket or portfolio */
  productId: number;

  /** Free-form JSON settings - we define structure via ProductTagsData */
  settingsJson: ProductTagsData;

  /** Timestamp when settings were created */
  createdAtUtc?: string;

  /** Timestamp when settings were last updated */
  updatedAtUtc?: string;
}

/**
 * Extended ProductSettings with provider context.
 * Use when you need to track which provider owns the product.
 * The providerId is added by frontend from request context, not from API.
 */
export interface ProductSettingsWithProvider extends ProductSettings {
  /** Provider ID - added by frontend from request context */
  providerId: number;
}

// ============================================================
// API Response Wrappers
// ============================================================

/**
 * Response from single product settings endpoints.
 * GET /v2/providers/:id/baskets/:basketId/settings
 * GET /v2/providers/:id/portfolios/:portfolioId/settings
 */
export interface ProductSettingsResponse {
  message: string;
  settings: ProductSettings;
}

/**
 * Response from list settings endpoints.
 * GET /v2/basket-settings/admin
 * GET /v2/basket-settings/provider
 * GET /v2/basket-settings/investor
 * GET /v2/portfolio-settings/admin
 * GET /v2/portfolio-settings/provider
 * GET /v2/portfolio-settings/investor
 * GET /v2/providers/:id/basket-settings/*
 * GET /v2/providers/:id/portfolio-settings/*
 */
export interface ProductSettingsListResponse {
  message: string;
  settings: ProductSettings[];
}

// ============================================================
// Request Types
// ============================================================

/**
 * Request body for upsert settings endpoints.
 * POST /v2/providers/:id/baskets/:basketId/settings
 * POST /v2/providers/:id/portfolios/:portfolioId/settings
 *
 * The request body is sent directly as the settingsJson content.
 * Backend stores whatever we send in the settingsJson field.
 */
export type UpsertSettingsRequest = ProductTagsData;

// ============================================================
// Helper Types for UI Components
// ============================================================

/**
 * Tag display mode for UI components.
 * - 'badges': Full badge display with labels
 * - 'icons': Compact icon-only display
 * - 'full': Detailed display with descriptions
 */
export type TagDisplayMode = 'badges' | 'icons' | 'full';

/**
 * Risk level category derived from risk score.
 * Used for color coding in UI.
 */
export type RiskLevel = 'low' | 'medium' | 'high';

// ============================================================
// Helper Functions
// ============================================================

/**
 * Determines the risk level category from a numeric risk score.
 * Used for color coding badges in the UI.
 *
 * @param score - Risk score from 1-10
 * @returns Risk level category: 'low' (1-3), 'medium' (4-6), 'high' (7-10)
 */
export function getRiskLevel(score: number | undefined): RiskLevel {
  if (score === undefined || score === null) {
    return 'medium'; // Default when no score
  }
  if (score <= 3) {
    return 'low';
  }
  if (score <= 6) {
    return 'medium';
  }
  return 'high';
}

/**
 * Determines the risk level category from a RiskTypeValue string.
 * Used for color coding badges in the UI.
 *
 * @param riskType - Risk type string value
 * @returns Risk level category for color coding
 */
export function getRiskLevelFromType(riskType: RiskTypeValue | undefined): RiskLevel {
  if (!riskType) {
    return 'medium';
  }
  switch (riskType) {
    case 'Extremely High':
    case 'High':
      return 'high';
    case 'Medium':
      return 'medium';
    case 'Low':
    case 'Very Low':
      return 'low';
    default:
      return 'medium';
  }
}

/**
 * Checks if a ProductTagsData object has any tags set.
 * Used to determine if tags should be displayed.
 *
 * @param tags - The tags data object to check
 * @returns true if at least one tag has a value
 */
export function hasAnyTags(tags: ProductTagsData | undefined | null): boolean {
  if (!tags) {
    return false;
  }
  return (
    tags.risk !== undefined ||
    tags.focus !== undefined ||
    tags.riskType !== undefined ||
    tags.minInvestment !== undefined ||
    tags.featured !== undefined
  );
}

/**
 * Counts the number of tags that have values set.
 *
 * @param tags - The tags data object to count
 * @returns Number of tags with values (0-5)
 */
export function countTags(tags: ProductTagsData | undefined | null): number {
  if (!tags) {
    return 0;
  }
  let count = 0;
  if (tags.risk !== undefined) count++;
  if (tags.focus !== undefined) count++;
  if (tags.riskType !== undefined) count++;
  if (tags.minInvestment !== undefined) count++;
  if (tags.featured !== undefined) count++;
  return count;
}
